import React, { useMemo } from 'react';
import styles from './TrendingAudiosTable.module.css';
import PaginationTable, {
    PaginationTableProps,
} from '../../../../../../ui/DataDisplay/Tables/PaginationTable/PaginationTable';
import { TrendingAudiosTableRow } from '../TrendingAudios.types';
import { CellProps, Column } from 'react-table';
import cn from 'classnames';
import { formatDate, formatNumberToKNotation, getLastUpdatedLabel, roundTo2Dp } from '../../../../../../helpers';
import Skeleton from '../../../../../../ui/Skeleton/Skeleton';
import { GetTrendingTiktokAudioStatsParams, GetTrendingTiktokAudioStatsSortableKeys } from '@round/api';
import BarsSmallChart from '../../../../../../ui/DataDisplay/Charts/BarsSmallChart/BarsSmallChart';
import TruncatedTextCell from '../../../../../../SharedComponents/TableComponents/TruncatedTextCell/TruncatedTextCell';
import TiktokAudioBadge from '../../../../audios/components/TiktokAudioBadge/TiktokAudioBadge';

type TrendingAudiosTableProps = Omit<PaginationTableProps<TrendingAudiosTableRow>, 'columns'> & {
    ordering: GetTrendingTiktokAudioStatsParams['ordering'];
};

const TrendingAudiosTable = ({ ordering, ...props }: TrendingAudiosTableProps) => {
    const columns: Column<TrendingAudiosTableRow>[] = useMemo(
        () => [
            {
                Header: 'Sound',
                accessor: 'title',
                Cell: ({ row: { original } }) => {
                    return (
                        <TiktokAudioBadge
                            loading={props.loading}
                            audio={{
                                tiktokId: original.tiktok_id,
                                title: original.title,
                                imageUrl: original.imageUrl,
                                audioPlayUrl: original.audioUrl,
                                isOriginal: original.is_original,
                            }}
                        />
                    );
                },
            },
            {
                Header: 'Author',
                accessor: 'author_name',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{value}</>;
                },
            },
            {
                Header: 'Date Posted',
                accessor: 'first_seen_at',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    const datePosted = new Date(value * 1000);
                    return (
                        <div className={styles.datePosted}>
                            <span>{formatDate(datePosted.toISOString())}</span>
                            <span className={styles.daysAgo}>{getLastUpdatedLabel(datePosted)}</span>
                        </div>
                    );
                },
            },
            {
                Header: 'Post Description',
                accessor: 'top_post_description',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <TruncatedTextCell>{value}</TruncatedTextCell>;
                },
            },
            {
                Header: 'Post Views',
                accessor: 'top_post_view_count',
                Cell: ({ value }) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    return <>{formatNumberToKNotation(value)}</>;
                },
            },
            {
                Header: 'Creations',
                id: 'creations',
                Cell: ({ row: { original } }: CellProps<TrendingAudiosTableRow>) => {
                    if (props.loading) {
                        return <Skeleton />;
                    }

                    const key = ordering?.slice(1) as GetTrendingTiktokAudioStatsSortableKeys;
                    let orderingValue = key.includes('relative')
                        ? `${roundTo2Dp(original[key] * 100)}%`
                        : formatNumberToKNotation(original[key]);

                    if (key === 'first_seen_at') {
                        orderingValue = formatNumberToKNotation(original['video_count_daily_change']);
                    }

                    return (
                        <div className={styles.creations}>
                            <span>{formatNumberToKNotation(original.video_count)}</span>
                            {key !== 'video_count' && (
                                <span className={cn(styles.badge, styles.creations)}>{orderingValue}</span>
                            )}
                            <div className={styles.creationsChart}>
                                <BarsSmallChart
                                    color="linear-gradient(180deg, #93AAFF 0%, #6BC1FF 100%)"
                                    values={original.standardised_data.daily_video_counts.slice(-7).map((i) => ({
                                        value: i.change,
                                        timestamp: i.timestamp,
                                    }))}
                                />
                            </div>
                        </div>
                    );
                },
            },
        ],
        [ordering, props.loading]
    );

    return <PaginationTable tableClassName={styles.table} columns={columns} {...props} />;
};

export default TrendingAudiosTable;
